// Backend configuration — the ONE place the API base URL lives.
//
// Never hard-code the backend URL in a component or service. Import
// API_BASE from here.
//
// The default is THE PAGE'S OWN ORIGIN (configurable identity, P1-3,
// 2026-10-29). Every deployment serves the SPA and proxies /anixify/ to Odoo
// on one origin (Nginx in production, staging/nginx.conf on staging), so the
// page's origin IS the backend and no deployment's domain is compiled in.
// On the live site this resolves to exactly the URL the old hard-coded
// default named, so every request goes where it always went.
//
// A dev server (`craco start` on :3000) is NOT the backend's origin: set
// REACT_APP_API_BASE in frontend/.env (git-ignored) to point it somewhere,
// e.g.
//
//     REACT_APP_API_BASE=http://localhost:8071
//
// The REACT_APP_ prefix is required and is not a style choice: this app is
// Create React App behind CRACO, not Vite. CRA's build inlines only
// REACT_APP_*-prefixed variables into the bundle — a VITE_*-prefixed name
// would silently be undefined here and fall through to the default.

const DEFAULT_API_BASE =
  typeof window !== "undefined" && window.location ? window.location.origin : "";

// Cast because CRA's bundled ProcessEnv type declares only NODE_ENV and
// PUBLIC_URL; REACT_APP_* names are injected at build time, not typed.
const fromEnv = (process.env as Record<string, string | undefined>)
  .REACT_APP_API_BASE;

export const API_BASE = (fromEnv || DEFAULT_API_BASE).replace(/\/+$/, "");

// ---- Authentication ------------------------------------------------------
//
// THE BUILD-TIME API KEY IS GONE FROM THIS APP.
//
// It used to be compiled into the bundle and sent on every request. That
// authenticated the *application*, never the person using it, and it was
// extractable by anyone who opened devtools — it was public the moment the
// app shipped. It has been replaced by a real per-user Odoo session:
// /anixify/login sets an HttpOnly, Secure, SameSite=Lax cookie the browser
// attaches automatically, and the backend decides what that user may do
// from their Anixify role.
//
// Do NOT reintroduce a key here. Requests carry credentials, not secrets:
// every fetch sets credentials:"include" and nothing else. There is no
// header to forget and no value to leak into the bundle.
//
// The cutover is FINISHED on both sides: the backend no longer accepts the
// old key either. X-Anixify-Api-Key was removed outright from
// controllers/access.py, and the role-access tests pin that a stray key
// header rescues nothing. There is no key-based rollback any more — a
// previously-deployed bundle that still sends one is simply unauthenticated.

// Auth endpoints, relative to API_BASE.
export const AUTH_ENDPOINTS = {
  login: "/anixify/login",
  logout: "/anixify/logout",
  whoami: "/anixify/whoami",
} as const;

// Backend endpoints, relative to API_BASE.
export const ENDPOINTS = {
  priceLines: "/anixify/api/price_lines",
  saveQuote: "/anixify/api/save_quote",
  quotes: "/anixify/api/quotes",
  // quotes-list-usability (2026-10-06): the Quotes screen's ONE read -- paged,
  // sorted and filtered on the server, scope enforced there.
  quoteList: "/anixify/api/quote_list",
  getQuote: "/anixify/api/get_quote",
  companySettings: "/anixify/api/company_settings",
  saveCompanySettings: "/anixify/api/save_company_settings",
  currencies: "/anixify/api/currencies",
  customers: "/anixify/api/customers",
  customerAddresses: "/anixify/api/customer_addresses",
  createCustomer: "/anixify/api/create_customer",
  // customer-details-on-quote: edit an existing customer company's billing
  // details (Sales/Admin), and the Indian states its form offers.
  updateCustomer: "/anixify/api/update_customer",
  states: "/anixify/api/states",
  // The CREATE-path duplicate check: which active customers hold a GSTIN,
  // exactly, at any size (customer-snapshot-at-finalise, 2026-09-23).
  customerGstinMatches: "/anixify/api/customer_gstin_matches",
  // The read-only sales report (Sales/Admin; a Viewer is refused).
  salesReport: "/anixify/api/sales_report",
  // Settings > Users: internal users with role, active and last sign-in.
  // Read only; Admin only (settings-users-tab).
  users: "/anixify/api/users",
  // settings-users (2026-09-30): ADMIN ONLY. Add, edit, deactivate/reactivate.
  // There is no delete route -- a user is only ever deactivated.
  createUser: "/anixify/api/create_user",
  updateUser: "/anixify/api/update_user",
  setUserActive: "/anixify/api/set_user_active",
  // password-reset (2026-09-30): an Admin sets ANOTHER user's password (Admin
  // only); anyone changes their OWN with the current one. Both end the user's
  // other sessions.
  resetUserPassword: "/anixify/api/reset_user_password",
  changeMyPassword: "/anixify/api/change_my_password",
  // Copy a quote's inputs into a new draft (copy-quote). Sales and Admin.
  copyQuote: "/anixify/api/copy_quote",
  // Margin approval (margin-approval). Request/withdraw: Sales. Approve/reject: Admin.
  requestApproval: "/anixify/api/request_approval",
  withdrawApproval: "/anixify/api/withdraw_approval",
  approveQuote: "/anixify/api/approve_quote",
  rejectQuote: "/anixify/api/reject_quote",
  // A finalised quote's outcome (quote-won-lost). Won/Lost: Sales (own) and
  // Admin. Reset to Open: Admin.
  markQuoteWon: "/anixify/api/mark_quote_won",
  markQuoteLost: "/anixify/api/mark_quote_lost",
  resetQuoteStatus: "/anixify/api/reset_quote_status",
  // Revise a finalised Open/Expired quote into <number>-R<n> (quote-revisions).
  // Sales (own) and Admin.
  reviseQuote: "/anixify/api/revise_quote",
  // Finalised Open quotes due within 7 days or past Valid Until, soonest
  // first (follow-up-list). GET, read only; Sales (own) and Admin.
  followUp: "/anixify/api/follow_up",
  // A quote's GST lines -- CGST+SGST, IGST, or one flat line (gst-split).
  // Read only, Viewer; computed server-side, frozen once finalised.
  quoteTax: "/anixify/api/quote_tax",
  // "Use this ship-to" from an extracted PO's Deliver-To code
  // (po-deliver-to-match). Sales (own) and Admin.
  setQuoteShipTo: "/anixify/api/set_quote_ship_to",
  // The real product master, read by the Add Materials picker. GET, viewer
  // role, ?q= searched against part_number/description and ?limit= capped
  // server-side. A READ route, so unlike the create_* endpoints it carries no
  // Sec-Fetch same-origin requirement.
  products: "/anixify/api/products",
  // Stock levels per product id. GET, viewer role, ?product_ids=CSV of integer
  // ids (omitted asks for everything the provider knows) and ?locations=1 adds
  // the per-location breakdown. A READ route, so like `products` above it
  // carries no Sec-Fetch same-origin requirement.
  //
  // ⚠️ ABSENT IS NOT ZERO, AND THAT IS THE WHOLE POINT OF THIS ENDPOINT. A
  // product with no key in the response has not been reported on; a key
  // holding 0 is somebody stating there is none. Asking about ten products
  // and getting one key back is the COMPLETE answer, not a partial one. See
  // getStock in services/api.ts, which is the only caller and the only place
  // that distinction is allowed to be collapsed (it isn't).
  //
  // ⚠️ IT IS KEYED BY PRODUCT ID, NEVER BY PART NUMBER. Part numbers are not
  // unique and the backend deliberately has no constraint saying they are, so
  // a map keyed on one merges two products into a single quantity. The
  // endpoint refuses a part-number token outright rather than guessing.
  stock: "/anixify/api/stock",
  // Create ONE product. POST, SALES role, and @state_changing on the backend --
  // so unlike the read above it demands same-origin evidence. There is no
  // find-or-create behind it: asking to create always creates, because part
  // numbers are legitimately not unique across manufacturers.
  createProduct: "/anixify/api/create_product",
  // Create MANY products in ONE transaction. POST, SALES role (the same act
  // in bulk, not a new kind of act), and @state_changing on the backend, so
  // it demands the same same-origin evidence create_product does.
  //
  // ⚠️ ALL OR NOTHING, CAPPED AT 200 ROWS, AND CREATE-ONLY. One bad row fails
  // the whole batch tagged `products[i]: ...`; an over-cap file is REFUSED and
  // never truncated; and re-sending a file DOUBLES the catalog, because
  // part_number is deliberately not unique. The "already exists" guard is
  // therefore the CLIENT's -- see services/csvImport.ts.
  importProducts: "/anixify/api/import_products",
  // PO DOCUMENTS (slice A). Storage only -- nothing behind these four reads a
  // document's contents. Comparing a PO against the quote is slice B.
  //
  // ROLES, and the split differs from the write routes above: reading is
  // VIEWER (a PO is the customer's own paperwork and anyone who may see the
  // quote may see what came back), writing is SALES -- not Admin, because
  // Sales are who the PO is actually sent to.
  //
  // quoteDocuments is a POST that only READS, like getQuote, so the quote id
  // travels in a JSON body rather than a query string.
  quoteDocuments: "/anixify/api/quote_documents",
  // ⚠️ A READ, DESPITE BEING A POST. It creates nothing -- the rows it returns
  // go to the SAME review table the paste path fills, and save_quote_po
  // remains the only writer. Sales-gated, because reading the CONTENTS of a
  // customer's paperwork is a different act from listing that it exists.
  extractPoDocument: "/anixify/api/extract_po_document",
  uploadQuoteDocument: "/anixify/api/upload_quote_document",
  // ⚠️ THE ONLY GET HERE, and the only endpoint in this app that answers with
  // bytes rather than JSON. It is served Content-Disposition: attachment with
  // nosniff and a locked-down CSP, so the browser downloads it and never
  // renders it inside this origin.
  deleteQuoteDocument: "/anixify/api/delete_quote_document",
  quoteDocument: "/anixify/api/quote_document",
  // CUSTOMER PO LINES AND THE COMPARISON (slice B).
  //
  // ⚠️ NOTHING BEHIND THESE READS A DOCUMENT. PO lines are TYPED BY A PERSON.
  // The comparison is the product; extraction is a replaceable input and
  // there is none in this build.
  //
  // Reading is VIEWER, writing is SALES -- the same split slice A settled.
  // compareQuotePo is a POST that only READS: it returns verdicts and writes
  // nothing, so it carries no state-changing marker on either side.
  quotePos: "/anixify/api/quote_pos",
  saveQuotePo: "/anixify/api/save_quote_po",
  compareQuotePo: "/anixify/api/compare_quote_po",
  deleteQuotePo: "/anixify/api/delete_quote_po",
  // ⚠️ THE ONLY ROUTE THAT DELETES A QUOTE, AND IT ONLY EVER DELETES AN EMPTY
  // DRAFT. Sales role, owner-or-Admin, and refused outright if the quote is
  // finalised or holds a line, a PO, a document or a shipment. It NEVER
  // removes a document itself: the caller deletes each one through
  // deleteQuotePo's sibling deleteQuoteDocument first, which is what records
  // who removed the paperwork. See models/quote.py delete_empty_draft.
  deleteQuote: "/anixify/api/delete_quote",
  // discard-draft (2026-09-30): remove a DRAFT with its lines. Own-or-Admin;
  // never finalised or awaiting approval; refused while it holds a PO, a
  // document or a shipment. One audit row.
  discardDraft: "/anixify/api/discard_draft",
  // The ACTIVE brand and category names a product may be classified with. GET,
  // viewer role, no parameters. Names, not ids -- the name IS the join key the
  // closed ProductBrand / ProductCategory unions compare with `===`.
  //
  // It returns the whole ACTIVE list, and since configurable identity P1-9
  // that list alone decides what a product may be classified with (brands
  // and categories are master data; there is no compiled list any more).
  taxonomy: "/anixify/api/taxonomy",
  // The three PUBLISHED SOURCING RATE TABLES. GET, viewer role, no
  // parameters, no @state_changing on the backend -- they write nothing.
  //
  // ⚠️ THESE ARE THE ROWS THE PRICER READS. anixify.quote.line resolves a
  // line's duty out of anixify.duty.rate and its conversion rate out of
  // anixify.fx.rate. Before these endpoints existed, /sourcing and
  // Settings > Exchange Rates rendered frontend mock arrays instead -- two
  // sources for one fact, disagreeing where nobody could see it. Never put a
  // mock back behind these screens.
  //
  // ⚠️ EVERY ROW COMES BACK, WITHDRAWN AND LAPSED ONES INCLUDED. The screens
  // have a Status column whose whole job is telling Active from
  // Superseded/Expired, so the backend passes active_test=False. A filter
  // offering "Superseded" that always matched nothing would be worse than no
  // filter.
  //
  // ⚠️ SELECTION FIELDS ARRIVE AS RAW KEYS ("sea", "per_cbm",
  // "customs_notified"), never as labels -- labels are translatable and the
  // SPA's unions compare with ===. Narrowing them is the CLIENT's job; see
  // toFreightRate / toFxRate in services/api.ts, which map an unrecognised
  // key to null rather than casting it through.
  //
  // There is NO write endpoint for any of the three, deliberately. Who may
  // publish a rate, and what happens to quotes already priced off the row
  // being edited, is a real design question that adding a POST would answer
  // by accident. The three detail panels keep their disabled ReadOnlySave.
  // The OEM discount rule table -- the STANDING rate card, keyed on
  // (partner_id, brand_id). GET, viewer role.
  //
  // ⚠️ IT HOLDS STANDING REGISTERED RATES ONLY. A competitive bid is a
  // negotiation, different every time, and is entered as a hand-typed net cost
  // on the LINE -- it must never be written here. Owner's instruction.
  //
  // ⚠️ ARCHIVED RULES COME BACK TOO. The screen has a Status column, and
  // Odoo's default record rule would drop them silently.
  discountRules: "/anixify/api/discount_rules",
  // Create or update ONE rule. POST, ADMIN role, and @state_changing on the
  // backend, so it demands same-origin evidence.
  //
  // ⚠️ ADMIN, NOT SALES, AND THAT IS COMMERCIAL RATHER THAN TECHNICAL. A rule
  // flows into every quote priced afterwards, so Sales creating one would let
  // the person negotiating a deal also set the floor for it.
  //
  // ⚠️ THERE IS NO DELETE. A rate is superseded by adding its replacement and
  // ARCHIVING this one (active: false), which keeps the record of what a line
  // was priced under.
  saveDiscountRule: "/anixify/api/save_discount_rule",
  dutyRates: "/anixify/api/duty_rates",
  freightRates: "/anixify/api/freight_rates",
  fxRates: "/anixify/api/fx_rates",
  // ⚠️ ADMIN ONLY, AND A PRICING INPUT. Publishing a rate closes the
  // currently open row's window at the day before and creates the new row, in
  // one transaction; a rate row is otherwise never edited. See
  // anixify.fx.rate.publish_rate.
  saveFxRate: "/anixify/api/save_fx_rate",
  // Correct a mistyped rate: today-or-later, never one a finalised quote
  // used, value only, audited. Admin only (fx-rate-correction).
  correctFxRate: "/anixify/api/correct_fx_rate",
  // duty-rate-entry (2026-09-30): ADMIN ONLY, same discipline as FX. Publish
  // one rate (closes the HSN's open window), publish an uploaded file's rows
  // all-or-nothing, preview an upload (writes nothing), correct a mistyped
  // same-day-or-later rate.
  saveDutyRate: "/anixify/api/save_duty_rate",
  saveDutyRates: "/anixify/api/save_duty_rates",
  previewDutyRates: "/anixify/api/preview_duty_rates",
  correctDutyRate: "/anixify/api/correct_duty_rate",
  // data-readiness (2026-09-30): ADMIN ONLY, READ ONLY. Every draft that
  // cannot price or finalise, grouped by reason.
  readiness: "/anixify/api/readiness",
  // warehouse-master (2026-10-01): the dispatch warehouses. The list is read
  // by every role; every write is ADMIN ONLY and there is no delete route.
  warehouses: "/anixify/api/warehouses",
  saveWarehouse: "/anixify/api/save_warehouse",
  setWarehouseActive: "/anixify/api/set_warehouse_active",
  previewWarehouses: "/anixify/api/preview_warehouses",
  importWarehouses: "/anixify/api/import_warehouses",
  // holiday-list (M1, 2026-11-03): the company holiday list. Read by every
  // role; every write is ADMIN ONLY and there is no delete route.
  holidays: "/anixify/api/holidays",
  saveHoliday: "/anixify/api/save_holiday",
  setHolidayActive: "/anixify/api/set_holiday_active",
  previewHolidays: "/anixify/api/preview_holidays",
  importHolidays: "/anixify/api/import_holidays",
  // oem-quote-register (2026-10-06): every role reads the register; Sales and
  // Admin write it. There is no delete route. The upload preview saves nothing.
  oemQuotes: "/anixify/api/oem_quotes",
  getOemQuote: "/anixify/api/get_oem_quote",
  saveOemQuote: "/anixify/api/save_oem_quote",
  previewOemQuoteLines: "/anixify/api/preview_oem_quote_lines",
  oemQuoteMatches: "/anixify/api/oem_quote_matches",
  // Order Management O1/O2 (2026-10-30): the OEM's confirmation per quote
  // line. Read: Viewer (read-only), Sales (OWN quotes), Admin. Write: Sales on
  // their own quotes, Admin on any. Nothing here prices.
  oemConfirmations: "/anixify/api/oem_confirmations",
  recordOemConfirmation: "/anixify/api/record_oem_confirmation",
  withdrawOemConfirmation: "/anixify/api/withdraw_oem_confirmation",
  // O3-O5: price requests, the ordering gate, OEM orders. Same scoping.
  oemRequests: "/anixify/api/oem_requests",
  createOemRequests: "/anixify/api/create_oem_requests",
  updateOemRequest: "/anixify/api/update_oem_request",
  acceptOemDifference: "/anixify/api/accept_oem_difference",
  oemOrderCheck: "/anixify/api/oem_order_check",
  createOemOrder: "/anixify/api/create_oem_order",
  oemOrders: "/anixify/api/oem_orders",
  getOemOrder: "/anixify/api/get_oem_order",
  // O6 / O7: OEM order events, a customer order's status, open OEM orders.
  recordOemOrderEvent: "/anixify/api/record_oem_order_event",
  orderStatus: "/anixify/api/order_status",
  openOemOrders: "/anixify/api/open_oem_orders",
  // M3.1 (owner B3): the customer-promised date per line of a Won quote.
  setCustomerDates: "/anixify/api/set_customer_dates",
  // M4 (O8-O11, owner D195): goods receipts, delivery notes, proof of
  // delivery, customs actuals, true margin (Admin), close / reopen (Admin).
  grns: "/anixify/api/grns",
  recordGrn: "/anixify/api/record_grn",
  reverseGrn: "/anixify/api/reverse_grn",
  deliveryNotes: "/anixify/api/delivery_notes",
  createDeliveryNote: "/anixify/api/create_delivery_note",
  reverseDeliveryNote: "/anixify/api/reverse_delivery_note",
  getDeliveryNote: "/anixify/api/get_delivery_note",
  attachPod: "/anixify/api/attach_pod",
  podDocuments: "/anixify/api/pod_documents",
  customsEntries: "/anixify/api/customs_entries",
  recordCustomsEntry: "/anixify/api/record_customs_entry",
  reverseCustomsEntry: "/anixify/api/reverse_customs_entry",
  trueMargin: "/anixify/api/true_margin",
  closeQuoteOrder: "/anixify/api/close_quote_order",
  reopenQuoteOrder: "/anixify/api/reopen_quote_order",
  // M2 (owner B4/B5, D197): the OEM master, aliases, offers, supplier choice.
  oems: "/anixify/api/oems",
  saveOem: "/anixify/api/save_oem",
  addOemAlias: "/anixify/api/add_oem_alias",
  mergeOem: "/anixify/api/merge_oem",
  setOemActive: "/anixify/api/set_oem_active",
  oemOffers: "/anixify/api/oem_offers",
  selectOemOffer: "/anixify/api/select_oem_offer",
} as const;
